"use client";

import { useEffect, useRef } from "react";
import { installComponentMonitoring } from "./component";
import type { ComponentRenderMonitorDefinition } from "./component";
import { installMonitoring } from "./core/runtime";
import type { InstallMonitoringOptions, PodMonitoringSchema } from "./core/types";
import { installInteractionMonitoring } from "./interaction";
import type { InteractionMonitoringConfig } from "./interaction";

export interface MonitoringRuntimeProps {
  schema: PodMonitoringSchema;
  componentMonitors?: ComponentRenderMonitorDefinition[];
  interactionOptions?: InteractionMonitoringConfig;
  observeFetch?: InstallMonitoringOptions["observeFetch"];
}

export function MonitoringRuntime({
  schema,
  componentMonitors,
  interactionOptions,
  observeFetch,
}: MonitoringRuntimeProps) {
  const initialPropsRef = useRef<MonitoringRuntimeProps | null>(null);

  if (initialPropsRef.current === null) {
    initialPropsRef.current = {
      schema,
      componentMonitors,
      interactionOptions,
      observeFetch,
    };
  }

  useEffect(() => {
    const initialProps = initialPropsRef.current ?? {
      schema,
      componentMonitors,
      interactionOptions,
      observeFetch,
    };
    const {
      schema: initialSchema,
      componentMonitors: initialComponentMonitors,
      interactionOptions: initialInteractionOptions,
      observeFetch: initialObserveFetch,
    } = initialProps;

    const cleanupMonitoring = installMonitoring(
      initialObserveFetch === undefined
        ? {
            schema: initialSchema,
          }
        : {
            schema: initialSchema,
            observeFetch: initialObserveFetch,
          }
    );

    const cleanupInteractionMonitoring = installInteractionMonitoring({
      schema: initialSchema,
      ...initialInteractionOptions,
    });

    const cleanupComponentMonitoring = initialComponentMonitors?.length
      ? installComponentMonitoring({
          schema: initialSchema,
          components: initialComponentMonitors,
        })
      : () => undefined;

    return () => {
      cleanupComponentMonitoring();
      cleanupInteractionMonitoring();
      cleanupMonitoring();
    };
  }, []);

  return null;
}
